import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import styles from "../styles/OrderSuccess.module.css";
import { getCookie, getFacebookTrackingData, getFacebookBrowserId, areCookiesEnabled } from "../utils/fbclid";
import { api, setApiRole } from "../api/api";
import { FaWhatsapp } from "react-icons/fa";
import metaConfig from "../utils/metaTrackingConfig";
import { initPixel } from "../utils/metaConversionsHelper";
const OrderSuccess = () => {
  // This page is always customer-facing — ensure api uses user token, not admin.
  // Prevents the 401 handler from redirecting to /admin/ if auth is missing.
  setApiRole('user');
  const { orderId } = useParams();
  const containerRef = useRef(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to process order ID
  const processOrderId = (rawOrderId) => {
    if (!rawOrderId) return null;
    
    // Remove "ORD_" prefix if present
    let processed = rawOrderId.replace(/^ORD_/i, '');
    
    // Remove leading zeros
    processed = processed.replace(/^0+/, '');
    
    // If all were zeros, return '0'
    if (!processed) processed = '0';
    
    console.log('Order ID processing:', {
      raw: rawOrderId,
      processed: processed
    });
    
    return processed;
  };

  // Fetch order details
  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!orderId) {
        setError('No order ID provided');
        setLoading(false);
        return;
      }

      // No auth token yet — the post-payment session (see CheckoutRedirectCallback's
      // retrievePaymentSession) may not have been created in time by the webhook.
      // Skip the authenticated call rather than firing a request that's guaranteed
      // to 401; fall through to the order-ID-only confirmation view below instead.
      if (!localStorage.getItem('userAccessToken')) {
        setLoading(false);
        return;
      }

      try {
        const processedOrderId = processOrderId(orderId);
        
        if (!processedOrderId) {
          setError('Invalid order ID format');
          setLoading(false);
          return;
        }

        console.log('Fetching order details for ID:', processedOrderId);
        
        const response = await api.get(`/orders/order/${processedOrderId}`);
        
        if (response.data.status === 'success') {
          setOrderDetails(response.data.data);
          console.log('Order details fetched:', response.data.data);
        } else {
          setError('Order not found');
          console.error('Order fetch failed:', response.data);
        }
      } catch (err) {
        setError('Failed to fetch order details');
        console.error('Order fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId]);

  // Meta Pixel and CAPI logic
  useEffect(() => {
    // Only proceed if we have order details
    if (!orderDetails || loading) return;

    // scroll and focus
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      containerRef.current.focus?.();
    }

    // ======= Meta Pixel + CAPI logic =======
    const fbclid = localStorage.getItem("fbclid");
    const processedOrderId = processOrderId(orderId);
    const eventId = processedOrderId; // use processed orderId for deduplication
    const orderAmount = orderDetails.amount || 500; // Use actual order amount

    // Get Facebook tracking data with fallback support
    const fbcData = getFacebookTrackingData();
    const fbc = fbcData ? fbcData.fbc : null;
    
    // Get Facebook browser ID with fallback support
    const fbpData = getFacebookBrowserId();
    const fbp = fbpData ? fbpData.fbp : null;

    // Log tracking data availability for debugging
    console.log("Facebook tracking data:", {
      orderId: processedOrderId,
      amount: orderAmount,
      fbclid: !!fbclid,
      fbp: !!fbp,
      fbc: !!fbc,
      fbcSource: fbcData?.source || "none",
      fbpSource: fbpData?.source || "none",
      cookiesEnabled: areCookiesEnabled()
    });

    // 1) Fire client-side Pixel event.
    // Ensure the pixel is actually loaded BEFORE checking window.fbq. On the Razorpay
    // redirect flow (netbanking/wallet) this page can mount before App.jsx's async
    // pixel init has finished, so window.fbq may not exist yet — without this wait the
    // event was silently dropped. eventId stays the bare orderId so this browser event
    // still deduplicates against the server-side CAPI event fired by the payment webhook.
    (async () => {
      await metaConfig.ready();
      if (metaConfig.isTrackingEnabled()) initPixel(metaConfig.getPixelId());

      if (window.fbq && eventId) {
        try {
          // Enhanced pixel tracking with actual order amount
          const pixelParams = { value: orderAmount, currency: 'INR' };
          const pixelOptions = { eventID: eventId };

          // Include available tracking data in pixel call
          if (fbc) pixelOptions.fbc = fbc;
          if (fbp) pixelOptions.fbp = fbp;

          window.fbq('track', 'Purchase', pixelParams, pixelOptions);
          console.log("Facebook Pixel Purchase event fired with:", pixelOptions);
        } catch (err) {
          console.warn("fbq error", err);
        }
      } else {
        console.warn("Facebook Pixel not available or missing eventId");
      }
    })();

    // Clear first-party visitor tracking ID now that the order is complete.
    // This ensures the next visit/form-fill starts a fresh landing_visit session.
    localStorage.removeItem('_lv_visitor_id');
  }, [orderId, orderDetails, loading]);
  return (
    <div className={styles.successContainer} ref={containerRef} tabIndex={-1}>
      <div className={styles.successCard}>
        <div className={styles.icon}>🎉</div>
        <h1 className={styles.title}>Order Successful!</h1>
        
        {loading ? (
          <div className={styles.loading}>
            <div className={styles.spinner}></div>
            <p>Loading order details...</p>
          </div>
        ) : error ? (
          <div className={styles.error}>
            <p>⚠️ {error}</p>
            <div className={styles.orderId}>
              <span>Order ID:</span>
              <span style={{ fontWeight: 700, color: '#f57c00', marginLeft: 8, fontSize: '1.2em' }}>{orderId}</span>
            </div>
          </div>
        ) : orderDetails ? (
          <>
            <div className={styles.orderId}>
              <span>Your Order ID:</span>
              <span style={{ fontWeight: 700, color: '#f57c00', marginLeft: 8, fontSize: '1.2em' }}>{orderId}</span>
            </div>
            
            <div className={styles.orderDetails}>
              <div className={styles.orderInfo}>
                <p><strong>Amount:</strong> ₹{orderDetails.amount}</p>
                {orderDetails.product_name && (
                  <p><strong>Product:</strong> {orderDetails.product_name}</p>
                )}
                {orderDetails.service_name && (
                  <p><strong>Service:</strong> {orderDetails.service_name}</p>
                )}
                {orderDetails.profile_fullname && (
                  <p><strong>Profile:</strong> {orderDetails.profile_fullname}</p>
                )}
                <p><strong>Status:</strong> {orderDetails.status}</p>
                {orderDetails.payment_status && (
                  <p><strong>Payment:</strong> {orderDetails.payment_status}</p>
                )}
              </div>
            </div>
            
            <button
              type="button"
              className={styles.trackBtn}
              onClick={() =>
                window.open(
                  'https://play.google.com/store/apps/details?id=com.astro.astro_shubh_app',
                  '_blank',
                  'noopener,noreferrer'
                )
              }
            >
              Track your order
            </button>

            <p className={styles.message}>
              Thank you for your purchase! Your order is confirmed.<br />
              Your report is being prepared by our experts and will be delivered on <FaWhatsapp
                    size={18}
                    style={{ color: "#25D366", marginRight: "4px" }}
                  /> WhatsApp within 24–72 hours. Please keep WhatsApp notifications on for the delivery message.
            </p>
          </>
        ) : (
          <div className={styles.orderId}>
            <span>Your Order ID:</span>
            <span style={{ fontWeight: 700, color: '#f57c00', marginLeft: 8, fontSize: '1.2em' }}>{orderId}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderSuccess;
